import { useCallback, useEffect, useMemo, useState, ReactNode } from 'react';
import { EventDto, FacebookPixelEvents, FacebookPixelEventType } from '@sweatpals/shared';
import { useRouter } from 'next/router';
import Head from 'next/head';
import * as fbq from '../lib/fb-pixel';
import { APP_ENV } from '../constants';

export const useFacebookPixel = (props?: { event?: EventDto }): {
	enableFacebookPixel: boolean;
	FB_PIXEL_ID: string;
	fbPixelNoScript: ReactNode;
	fbTrackPageView(): void;
	fbTrackEventTyped<T extends FacebookPixelEvents>(eventName: T, metadata: FacebookPixelEventType<T>): void;
} => {
	const { event } = props ?? {};

	const router = useRouter();

	const [FB_PIXEL_ID, setFB_PIXEL_ID] = useState<string>('');

	const enableFacebookPixel = !!FB_PIXEL_ID;

	const getFbPixelId = useCallback(() => {
		// enabled only on prod environment
		if (APP_ENV !== 'main') {
			return;
		}

		if (event?.additionalData?.tracking?.fbPixelId) {
			setFB_PIXEL_ID(event.additionalData.tracking.fbPixelId);
		}
	}, [event?.additionalData?.tracking?.fbPixelId]);

	const fbTrackEventTyped = useCallback(<T extends FacebookPixelEvents>(eventName: T, metadata: FacebookPixelEventType<T>) => {
		fbq.track(eventName, metadata);
	}, []);

	const fbTrackPageView = useCallback(() => {
		fbTrackEventTyped('PageView', undefined);
	}, [fbTrackEventTyped]);

	useEffect(() => {
		getFbPixelId();
	});

	const handleRouteChange = useCallback(() => {
		getFbPixelId();
		// This pageview triggers every time the route is changed via the app router
		fbTrackPageView();
	}, [getFbPixelId, fbTrackPageView]);

	useEffect(() => {
		if (FB_PIXEL_ID) {
			fbq.initFBPixel([FB_PIXEL_ID]);
		}

		// This pageview only triggers the first time (it's important for Pixel to have real information)
		fbTrackPageView();

		router.events.on('routeChangeComplete', handleRouteChange);

		return () => {
			router.events.off('routeChangeComplete', handleRouteChange);
		};
	}, [router.events, fbTrackPageView, handleRouteChange, FB_PIXEL_ID]);

	const fbPixelNoScript = useMemo(
		() =>
			enableFacebookPixel && (
				<Head>
					<noscript>
						<img
							height='1'
							width='1'
							style={{ display: 'none' }}
							src={`https://www.facebook.com/tr?id=${FB_PIXEL_ID}&ev=PageView&noscript=1`}
							alt='page-view'
						/>
					</noscript>
				</Head>
			),
		[enableFacebookPixel, FB_PIXEL_ID]
	);

	return { enableFacebookPixel, FB_PIXEL_ID, fbPixelNoScript, fbTrackPageView, fbTrackEventTyped };
};
